import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, Plus, Search } from "lucide-react";

import { templatesApi } from "@/api/careplans";
import { Alert } from "@/components/Alert";
import { EmptyState } from "@/components/EmptyState";
import { FormSelect } from "@/components/FormSelect";
import { ListToolbar } from "@/components/ListToolbar";
import { PageBody, PageHeader, PageShell } from "@/components/PageShell";
import { PageSkeleton } from "@/components/PageSkeleton";
import { Button } from "@/components/ui/button";

import { TemplateCard } from "./TemplateCard";

const CATEGORY_OPTIONS = [
  { value: "", label: "All Categories" },
  { value: "personal_care", label: "Personal Care" },
  { value: "medication", label: "Medication" },
  { value: "mobility", label: "Mobility" },
  { value: "companionship", label: "Companionship" },
  { value: "nursing", label: "Nursing" },
  { value: "custom", label: "Custom" },
];

export function TemplatesListPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");

  const loadTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (search) params.search = search;
      if (category) params.category = category;
      const res = await templatesApi.list(params);
      setTemplates(res.results || []);
    } catch (err) {
      setError(err.message || "Failed to load templates.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, [search, category]);

  if (loading && templates.length === 0) {
    return <PageSkeleton />;
  }

  return (
    <PageShell>
      <PageHeader
        title="Care Plan Templates"
        description="Manage reusable care templates across your agency roster."
        actions={
          <Button asChild>
            <Link to="/care-plans/templates/new">
              <Plus />
              Create Template
            </Link>
          </Button>
        }
      />

      <PageBody>
        <ListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search templates by name…"
          searchAriaLabel="Search templates"
        >
          <div className="w-full max-w-xs">
            <FormSelect
              id="templates-category-filter"
              value={category}
              onValueChange={setCategory}
              options={CATEGORY_OPTIONS}
              placeholder="All Categories"
            />
          </div>
        </ListToolbar>

        <Alert>{error}</Alert>

        {templates.length === 0 ? (
          <EmptyState
            icon={search || category ? Search : ClipboardList}
            tone={search || category ? "slate" : "amber"}
            title={
              search || category
                ? "No templates match those filters."
                : "No care plan templates yet"
            }
            message={
              search || category
                ? "Try adjusting your search or category filter."
                : "Create a template to reuse care checklists across clients."
            }
            action={
              search || category ? null : (
                <Button asChild>
                  <Link to="/care-plans/templates/new">
                    <Plus />
                    Create Template
                  </Link>
                </Button>
              )
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {templates.map((tpl) => (
              <TemplateCard key={tpl.id} template={tpl} />
            ))}
          </div>
        )}
      </PageBody>
    </PageShell>
  );
}
